import {
	createContext,
	ReactNode,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';

import { useApp } from './app';

export interface CartItem {
	id: number;
	barcode: string;
	name: string;
	price: number;
	coast?: number;
	stock?: number;
	unit?: string;
	quantity: number;
	isWeight?: boolean;
}

interface CartContextProps {
	items: CartItem[];
	addItem: (item: Omit<CartItem, 'quantity'>, quantity: number) => void;
	removeItem: (id: number) => void;
	updateQuantity: (id: number, quantity: number) => void;
	clearCart: () => void;
	totalItems: number;
	totalPrice: number;
	totalCost: number;
}

const CartContext = createContext<CartContextProps>({} as CartContextProps);

export const useCart = () => useContext(CartContext);

const CART_STORAGE_KEY = 'consulta-preco-cart';

export function CartProvider({ children }: { children: ReactNode }) {
	const { auth } = useApp();
	const [items, setItems] = useState<CartItem[]>(() => {
		const stored = localStorage.getItem(CART_STORAGE_KEY);
		return stored ? JSON.parse(stored) : [];
	});

	useEffect(() => {
		localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
	}, [items]);

	// Sem token, ou com o token de OUTRA loja, o carrinho recomeça: o que foi
	// montado numa loja não pode aparecer na outra.
	const lastToken = useRef(auth.token);
	useEffect(() => {
		const changedStore =
			Boolean(lastToken.current) && lastToken.current !== auth.token;
		if (!auth.token || changedStore) {
			setItems([]);
		}
		lastToken.current = auth.token;
	}, [auth.token]);

	const addItem = (item: Omit<CartItem, 'quantity'>, quantity: number) => {
		setItems((prev) => {
			const existingItem = prev.find((i) => i.id === item.id);

			if (existingItem) {
				return prev.map((i) =>
					i.id === item.id
						? {
								...i,
								coast: item.coast,
								stock: item.stock,
								quantity: i.quantity + quantity,
							}
						: i,
				);
			}

			return [
				...prev,
				{
					...item,
					quantity,
					isWeight: item.unit?.toUpperCase() === 'KG',
				},
			];
		});
	};

	const removeItem = (id: number) => {
		setItems((prev) => prev.filter((item) => item.id !== id));
	};

	const updateQuantity = (id: number, quantity: number) => {
		if (quantity <= 0) {
			removeItem(id);
			return;
		}

		setItems((prev) =>
			prev.map((item) => (item.id === id ? { ...item, quantity } : item)),
		);
	};

	const clearCart = () => {
		setItems([]);
	};

	const totalItems = items.reduce((acc, item) => acc + item.quantity, 0);
	const totalPrice = items.reduce(
		(acc, item) => acc + item.price * item.quantity,
		0,
	);
	const totalCost = items.reduce(
		(acc, item) => acc + (item.coast ?? 0) * item.quantity,
		0,
	);

	return (
		<CartContext.Provider
			value={{
				items,
				addItem,
				removeItem,
				updateQuantity,
				clearCart,
				totalItems,
				totalPrice,
				totalCost,
			}}
		>
			{children}
		</CartContext.Provider>
	);
}
